// src/components/ui/Accordion.jsx
import React, { useId, useRef, useEffect } from "react";

/**
 * AccordionItem
 * Props:
 *  id: string
 *  title: string | ReactNode
 *  open: boolean
 *  onToggle: (nextOpen: boolean) => void
 *  children: panel content
 *  scrollIntoViewOnOpen?: boolean
 */
export function AccordionItem({
  id,
  title,
  open,
  onToggle,
  children,
  scrollIntoViewOnOpen = false,
}) {
  const btnId = useId();
  const panelRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    if (open && scrollIntoViewOnOpen && headerRef.current) {
      // Ensure newly opened item is visible (great inside bottom sheets)
      headerRef.current.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [open, scrollIntoViewOnOpen]);

  return (
    <div className="border-b border-border/60">
      <h3 ref={headerRef}>
        <button
          type="button"
          id={`${btnId}-${id}-button`}
          aria-controls={`${btnId}-${id}-panel`}
          aria-expanded={open ? "true" : "false"}
          onClick={() => onToggle(!open)}
          className="w-full flex items-center justify-between py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
        >
          <span className="font-medium text-typography">{title}</span>
          <span
            className={`material-symbols-outlined transition-transform duration-200 ${
              open ? "rotate-180" : "rotate-0"
            }`}
            aria-hidden="true"
          >
            expand_more
          </span>
        </button>
      </h3>

      <div
        id={`${btnId}-${id}-panel`}
        role="region"
        aria-labelledby={`${btnId}-${id}-button`}
        ref={panelRef}
        className={`grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        } motion-reduce:transition-none`}
      >
        <div className="min-h-0">
          <div className="pb-4 pr-1 text-sm leading-relaxed text-typography/90">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Accordion
 * Props:
 *  items: Array<{ id: string, title: ReactNode, content: ReactNode }>
 *  allowMultiple?: boolean (default false -> one open at a time)
 *  defaultOpenIds?: string[]
 *  scrollIntoViewOnOpen?: boolean
 */
export function Accordion({
  items,
  allowMultiple = false,
  defaultOpenIds = [],
  scrollIntoViewOnOpen = false,
}) {
  const [openIds, setOpenIds] = React.useState(new Set(defaultOpenIds));

  const toggle = (id, nextOpen) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (allowMultiple) {
        if (nextOpen) next.add(id);
        else next.delete(id);
      } else {
        next.clear();
        if (nextOpen) next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="divide-y divide-border/60">
      {items.map((it) => (
        <AccordionItem
          key={it.id}
          id={it.id}
          title={it.title}
          open={openIds.has(it.id)}
          onToggle={(v) => toggle(it.id, v)}
          scrollIntoViewOnOpen={scrollIntoViewOnOpen}
        >
          {it.content}
        </AccordionItem>
      ))}
    </div>
  );
}
